import * as React from 'react';
import { SettingType } from './Setting';
import { ISettingSearchTarget } from './settingsSearch';
import { styleSheet } from './styles';

/** One built settings row: the searchable facts about it, plus the element that renders it. */
export interface ISettingEntry extends ISettingSearchTarget {
	/** The current value as short display text, for the group cards. Undefined when not summarisable. */
	valueText?: string;
	node: React.ReactElement;
}

/**
 * Best-effort plain text for a label, which may be a string, an element wrapping one at any depth,
 * or a component carrying its text as a `text` prop rather than a child.
 *
 * Reading only a string child covered most rows but left a handful nameless — an export button
 * (`<ExportDataButton text='Export Notes' />`), a button with an icon beside its span, a link
 * wrapping one. A row with no name cannot be counted on its group card, previewed, or found by
 * search, so the label is worth digging for.
 */
function getLabelText(label: unknown): string | undefined {
	if (typeof label === 'string') {
		return label.trim() || undefined;
	}
	if (Array.isArray(label)) {
		for (const child of label) {
			const text = getLabelText(child);
			if (text) {
				return text;
			}
		}
		return undefined;
	}
	if (React.isValidElement(label)) {
		const { children, text } = label.props as { children?: unknown; text?: unknown };
		// Children first: a component given both is naming itself with the child.
		return getLabelText(children) ?? getLabelText(text);
	}
	return undefined;
}

/**
 * The row's current value, short enough to sit on a group card. Read-only summary — the row
 * itself remains the only place a value can be changed.
 */
function getValueText(data: {
	type?: SettingType;
	value?: boolean | (() => boolean);
	selectedOption?: { text?: string };
	selectedOptions?: unknown[];
	inputProps?: { value?: unknown };
}): string | undefined {
	const booleanValue = typeof data.value === 'function' ? data.value() : data.value;

	switch (data.type) {
		case SettingType.Toggle:
			return booleanValue ? 'On' : 'Off';
		case SettingType.Multi:
		case SettingType.MultiWInput:
			// A toggle that reveals further controls; the toggle is the headline state.
			return booleanValue ? 'On' : 'Off';
		case SettingType.Dropdown:
			return data.selectedOption?.text || undefined;
		case SettingType.MultiSelect:
			return data.selectedOptions?.length ? `${data.selectedOptions.length} selected` : 'None';
		case SettingType.Input: {
			const value = data.inputProps?.value;
			return value === undefined || value === null || value === '' ? undefined : String(value);
		}
		default:
			return undefined;
	}
}

/**
 * Lift the searchable facts off a built row so grouping and search can work on data.
 *
 * Reading them back off the element is transitional: the row's `data` object is already the
 * descriptor we want, but it is written inline at ~155 construction sites. Deriving here keeps
 * that to one place instead of a filter that re-derived it per category on every render. Rows
 * declaring `category` on their descriptor and `isSubRow` explicitly can skip this entirely.
 */
export function toSettingEntry(node: React.ReactNode): ISettingEntry | null {
	if (!React.isValidElement(node)) {
		return null;
	}
	const { data, styles } = node.props as {
		data?: ISettingSearchTarget & { label?: unknown } & Parameters<typeof getValueText>[0];
		styles?: unknown[];
	};
	if (!data?.category) {
		return null;
	}
	return {
		category: data.category,
		labelText: getLabelText(data.label),
		info: data.info,
		valueText: getValueText(data),
		// Sub-rows mark themselves by carrying the shared subRow style, so identity is the only
		// signal available until the construction sites declare the relationship directly.
		isSubRow: Array.isArray(styles) && styles.some(style => style === styleSheet.subRow),
		node,
	};
}

/**
 * Stable, unique identity for a row.
 *
 * Every row is built with an explicit element key, so that is unique by construction. A label is
 * not: two custom dashboard cards can carry the same display name. Keying siblings by label let the
 * duplicate overwrite its twin in React's child map, leaving the untracked one undeleted at the head
 * of whichever group rendered next.
 */
export function getEntryKey(entry: ISettingEntry, index: number): string {
	return String(entry.node.key ?? entry.labelText ?? index);
}

interface IDetailRowOptions {
	badge?: React.ReactNode;
	/** For rows inside a chunk that draws its own separating line. */
	hideDivider?: boolean;
}

/**
 * Render an entry as a detail row: sized to its content, help text beneath the label, and no
 * collapsing wrapper. Both the group view and search results use this, so a disabled dependent
 * is greyed rather than animated to zero height in either place.
 */
export function toDetailRow(entry: ISettingEntry, { badge, hideDivider }: IDetailRowOptions = {}): React.ReactElement {
	const existingStyles = (entry.node.props as { styles?: unknown[] }).styles ?? [];
	return React.cloneElement(entry.node, {
		variant: 'detail',
		badge,
		// Appended, so the row keeps whatever styles it was built with.
		styles: hideDivider ? [...existingStyles, styleSheet.noDivider] : existingStyles,
	});
}
